import type { jsPDF } from "jspdf";
import {
  getLayerTable,
  hexToRgb,
  normRectToPdfMm,
  resolveTextStyle,
  type SurfLayer,
  type SurfStyle,
} from "@vida/surf-core";

function drawTableGrid(
  pdf: jsPDF,
  rect: { x: number; y: number; w: number; h: number },
  colCount: number,
  rowCount: number,
  strokeRgb: [number, number, number],
): { cellW: number; cellH: number } {
  const cellW = rect.w / colCount;
  const cellH = rect.h / rowCount;
  pdf.setDrawColor(...strokeRgb);
  pdf.setLineWidth(0.2);
  for (let r = 0; r <= rowCount; r += 1) {
    const y = rect.y + r * cellH;
    pdf.line(rect.x, y, rect.x + rect.w, y);
  }
  for (let c = 0; c <= colCount; c += 1) {
    const x = rect.x + c * cellW;
    pdf.line(x, rect.y, x, rect.y + rect.h);
  }
  return { cellW, cellH };
}

export function drawTableLayer(
  pdf: jsPDF,
  layer: SurfLayer,
  theme: SurfStyle,
  rect: ReturnType<typeof normRectToPdfMm>,
): void {
  const data = getLayerTable(layer);
  const colCount = Math.max(data.columns.length, 1);
  const rowCount = data.rows.length + 1;
  const bodyTs = resolveTextStyle({ ...layer, type: "text", textRole: "body" }, theme);
  const fontSize = Math.max(7, Math.min(11, (rect.h / rowCount) * 2.2));
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(fontSize);

  const [headerR, headerG, headerB] = hexToRgb(theme.palette.muted);
  const [textR, textG, textB] = hexToRgb(theme.palette.text);
  const strokeRgb = hexToRgb(theme.palette.muted) as [number, number, number];

  const { cellW, cellH } = drawTableGrid(pdf, rect, colCount, rowCount, strokeRgb);

  const padX = 1.2;
  const padY = cellH * 0.28;

  for (let c = 0; c < colCount; c += 1) {
    const cx = rect.x + c * cellW;
    const cy = rect.y;
    pdf.setFillColor(headerR, headerG, headerB);
    pdf.rect(cx, cy, cellW, cellH, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(textR, textG, textB);
    const headerText = data.columns[c] ?? "";
    const lines = pdf.splitTextToSize(headerText, cellW - padX * 2) as string[];
    pdf.text(lines[0] ?? "", cx + padX, cy + padY, { baseline: "top" });
  }

  pdf.setFont("helvetica", "normal");
  for (let r = 0; r < data.rows.length; r += 1) {
    for (let c = 0; c < colCount; c += 1) {
      const cx = rect.x + c * cellW;
      const cy = rect.y + (r + 1) * cellH;
      pdf.setTextColor(textR, textG, textB);
      const cellText = data.rows[r]?.[c] ?? "";
      const lines = pdf.splitTextToSize(cellText, cellW - padX * 2) as string[];
      pdf.text(lines[0] ?? "", cx + padX, cy + padY, { baseline: "top" });
    }
  }

  void bodyTs;
  void layer.rotation;
}
